/**
 * BudgetSheets - the Budgets tab on Notes & Reminders.
 *
 * Free-form budget planning, separate from the monthly budget: any row names,
 * a note on any row, and sheets that are kept rather than tied to a month or
 * a year. The list and an open sheet both live here; NotesTab only hides its
 * own header and tab bar while a sheet is open, the same way it does for an
 * open folder.
 *
 * Worked-out rows (patch 02): see budgetSheetMath.js for the rules.
 * Patch 03: delete warnings, blocked moves, drag to reorder rows, the
 * person's own currency, the \u24d8 explainer and the read-only example.
 *
 * Every sub-component is declared at module scope. One declared inside another
 * is a new type on each render, and React would remount it and lose whatever
 * was being typed (see the note on Sheet in plansShared).
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import customToast from "../../utils/customToast";
import { getCurrentCurrency } from "../../utils/formatters";
import { Check, ChevronDown, ChevronLeft, ChevronRight, GripVertical, MoreHorizontal, Plus } from "lucide-react";
import {
  Sheet,
  SHEET_FIELD_LABEL,
  SHEET_INPUT_CLS,
  SHEET_INPUT_STYLE,
  prettyShort,
  ConfirmDialog,
} from "./plansShared";
import { todayLocalKey, addDays } from "../../utils/plansUtils";
import {
  computeSheet, settleSheet, refsAbove, withRow, refLabel, describeCalc,
  parseAmount, formatMoney, newId, joinNames, dependentsOf, moveBreaks, rowRef, secRef,
} from "../../utils/budgetSheetMath";
import { EXAMPLE_ID, EXAMPLE_SHEET } from "./budgetSheetExample";
import { usePlan } from "../../utils/plan"; // plan_06

const INK = '#1F2937';
const RED = '#E11D48';
const SERIF = { fontFamily: "'DM Serif Display', serif", fontWeight: 400 };

// Tile slots in the order sections take them: Income pink (0), Saved green
// (2), Spent blue (1), Leftover lilac (3). Slots, not hexes, so each theme
// supplies its own four colours.
const SECTION_SLOTS = [0, 2, 1, 3];

/** Half-way from a tile gradient's first colour to white - the pale tint. */
const softTint = (bg) => {
  const m = String(bg).match(/#([0-9a-fA-F]{6})/);
  if (!m) return '#F7F3F5';
  const n = parseInt(m[1], 16);
  const mix = (c) => Math.round(c + (255 - c) * 0.5);
  const rgb = [mix((n >> 16) & 255), mix((n >> 8) & 255), mix(n & 255)];
  return '#' + rgb.map((x) => x.toString(16).padStart(2, '0')).join('');
};

const SHEET_EMOJIS = [
  '🗓️', '💰', '🏠', '🧾', '💳', '🚗', '✈️', '🎄',
  '🎓', '👶', '💍', '🛒', '📦', '🏖️', '🎁', '📈',
];

const relDay = (key, todayKey) => {
  if (!key) return '';
  if (key === todayKey) return 'today';
  if (key === addDays(todayKey, -1)) return 'yesterday';
  return prettyShort(key);
};

const Toggle = ({ on, accent }) => (
  <span
    className="flex-shrink-0 rounded-full relative"
    style={{ width: '44px', height: '24px', background: on ? (accent === '#1F1E24' ? '#9ED9B6' : accent) : '#D9D2D6', transition: 'background .15s ease' }}
  >
    <span
      className="absolute bg-white rounded-full"
      style={{
        width: '20px', height: '20px', top: '2px',
        left: on ? '22px' : '2px',
        transition: 'left .15s ease',
        boxShadow: '0 1px 3px rgba(0,0,0,.15)',
      }}
    />
  </span>
);

const PrimaryButton = ({ disabled, accent, onClick, children }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className="w-full rounded-full py-4 text-[15px] font-bold text-white"
    style={{ background: disabled ? '#E0D6DC' : accent }}
  >
    {children}
  </button>
);

/** Delete asks in a pop-up (ConfirmDialog in plansShared) - never a second tap in the same spot. */
const DeleteButton = ({ label, title, body, warning, onDelete }) => {
  const [asking, setAsking] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setAsking(true)}
        className="w-full mt-4 py-2 text-[14px] font-semibold"
        style={{ color: RED }}
      >
        {label}
      </button>
      {asking && (
        <ConfirmDialog
          title={title}
          body={body}
          warning={warning}
          onCancel={() => setAsking(false)}
          onConfirm={() => { setAsking(false); onDelete(); }}
        />
      )}
    </>
  );
};

/** Copy for a delete that other rows depend on. */
const dependWarning = (names) => {
  if (!names.length) return null;
  const many = names.length > 1;
  return joinNames(names) + (many ? ' use' : ' uses') + ' this. If you delete it, '
    + (many ? 'they\u2019ll' : 'it\u2019ll') + ' keep ' + (many ? 'their current amounts' : 'its current amount')
    + ' and stop updating.';
};

/** Copy for a move that is blocked. */
const blockedMove = (names) => joinNames(names) + ' can only use rows above '
  + (names.length > 1 ? 'them' : 'it') + ', so this can\u2019t move';

const InfoDot = ({ onClick, label = 'How this works' }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    style={{ background: '#F4F1F8', border: '1px solid #EFEAF1', borderRadius: '50%', width: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#6B6B73', padding: 0, flexShrink: 0, fontFamily: "'DM Sans', sans-serif" }}
  >
    i
  </button>
);

const MenuRow = ({ label, sub, right, onClick, first, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={'w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left'
      + (first ? '' : ' border-t theme-border-100')}
    style={{ opacity: disabled ? 0.4 : 1 }}
  >
    <span className="min-w-0">
      <span className="block text-[14.5px]" style={{ color: INK, fontWeight: 500 }}>{label}</span>
      {sub && <span className="block text-[12px] text-gray-500 mt-0.5 leading-snug">{sub}</span>}
    </span>
    {right}
  </button>
);

const FieldLabel = ({ children, className = '' }) => (
  <label className={SHEET_FIELD_LABEL + ' ' + className} style={{ color: INK }}>{children}</label>
);

/* ------------------------------------------------------------------ *
 * Sheet: create, or rename / pin / duplicate / delete
 * ------------------------------------------------------------------ */

const SheetForm = ({ sheet, accent, track, onSave, onPin, onDuplicate, onDelete, onClose }) => {
  const [name, setName] = useState(sheet ? sheet.name || '' : '');
  const [emoji, setEmoji] = useState(sheet ? sheet.emoji || SHEET_EMOJIS[0] : SHEET_EMOJIS[0]);
  const trimmed = name.trim();

  return (
    <Sheet
      title={sheet ? 'Sheet options' : 'New budget sheet'}
      subtitle={sheet ? null : 'Name it, then add sections and rows.'}
      onClose={onClose}
      track={track}
    >
      <FieldLabel>Name</FieldLabel>
      <input
        className={SHEET_INPUT_CLS}
        style={SHEET_INPUT_STYLE}
        value={name}
        maxLength={40}
        placeholder="e.g. Monthly setup"
        onChange={(e) => setName(e.target.value)}
      />

      <FieldLabel className="mt-4">Icon</FieldLabel>
      <div className="grid grid-cols-8 gap-1.5 mb-5">
        {SHEET_EMOJIS.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => setEmoji(e)}
            className="h-10 rounded-xl flex items-center justify-center text-[20px]"
            style={{
              background: emoji === e ? track : 'transparent',
              boxShadow: emoji === e ? 'inset 0 0 0 1.5px ' + accent : 'none',
            }}
          >
            {e}
          </button>
        ))}
      </div>

      <PrimaryButton disabled={!trimmed} accent={accent} onClick={() => onSave({ name: trimmed, emoji })}>
        {sheet ? 'Save' : 'Create sheet'}
      </PrimaryButton>

      {sheet && (
        <>
          <div className="mt-5 rounded-2xl border theme-border-100 overflow-hidden">
            <MenuRow
              first
              label="Pin to the top"
              sub="Pinned sheets sit first in the list"
              right={<Toggle on={!!sheet.pinned} accent={accent} />}
              onClick={onPin}
            />
            <MenuRow
              label="Duplicate"
              sub="Try out changes without touching this one"
              right={<ChevronRight className="w-4 h-4 text-gray-400" />}
              onClick={onDuplicate}
            />
          </div>
          <DeleteButton
            label="Delete sheet"
            title={'Delete \u201c' + (sheet.name || 'this sheet') + '\u201d?'}
            body="The whole sheet and everything in it will be deleted. This can't be undone."
            onDelete={onDelete}
          />
        </>
      )}
    </Sheet>
  );
};

/* ------------------------------------------------------------------ *
 * Section: title, total on/off, order, delete
 * ------------------------------------------------------------------ */

const SectionForm = ({
  section, index, count, accent, track, onSave, onMove, onDelete, onClose,
  blockUp, blockDown, onReorder, deleteWarning,
}) => {
  const [title, setTitle] = useState(section ? section.title || '' : '');
  const [showTotal, setShowTotal] = useState(section ? section.showTotal !== false : true);
  const trimmed = title.trim();
  const rowCount = section ? (section.rows || []).length : 0;

  return (
    <Sheet
      title={section ? 'Section options' : 'New section'}
      subtitle={section ? null : 'For example Income, Bills or Savings.'}
      onClose={onClose}
      track={track}
    >
      <FieldLabel>Section name</FieldLabel>
      <input
        className={SHEET_INPUT_CLS}
        style={SHEET_INPUT_STYLE}
        value={title}
        maxLength={40}
        placeholder="e.g. Income"
        onChange={(e) => setTitle(e.target.value)}
      />

      <div className="mt-4 mb-5 rounded-2xl border theme-border-100 overflow-hidden">
        <MenuRow
          first
          label="Show a total"
          sub="Turn off when adding these rows up wouldn't mean anything"
          right={<Toggle on={showTotal} accent={accent} />}
          onClick={() => setShowTotal(!showTotal)}
        />
      </div>

      <PrimaryButton disabled={!trimmed} accent={accent} onClick={() => onSave({ title: trimmed, showTotal })}>
        {section ? 'Save' : 'Add section'}
      </PrimaryButton>

      {section && (
        <>
          <div className="mt-5 rounded-2xl border theme-border-100 overflow-hidden">
            <MenuRow first label="Move up" disabled={index === 0 || !!blockUp} onClick={() => onMove(-1)}
              sub={index > 0 && blockUp ? blockedMove(blockUp) : null}
              right={<span className="text-gray-400">{'↑'}</span>} />
            <MenuRow label="Move down" disabled={index >= count - 1 || !!blockDown} onClick={() => onMove(1)}
              sub={index < count - 1 && blockDown ? blockedMove(blockDown) : null}
              right={<span className="text-gray-400">{'↓'}</span>} />
            {rowCount > 1 && (
              <MenuRow label="Reorder rows" sub="Drag rows into a new order" onClick={onReorder}
                right={<GripVertical className="w-4 h-4 text-gray-400" />} />
            )}
          </div>
          <DeleteButton
            label="Delete section"
            title={'Delete \u201c' + (section.title || 'this section') + '\u201d?'}
            body={(rowCount > 0
              ? 'This section and its ' + rowCount + ' row' + (rowCount === 1 ? '' : 's') + ' will be deleted.'
              : 'This section will be deleted.') + " This can't be undone."}
            warning={deleteWarning}
            onDelete={onDelete}
          />
        </>
      )}
    </Sheet>
  );
};

/* ------------------------------------------------------------------ *
 * Row editor: name, how it's worked out, note, counted or not
 * ------------------------------------------------------------------ */

const MODES = [
  { key: 'typed', emoji: '✏️', label: "I'll type it", sub: 'A number you enter' },
  { key: 'sum', emoji: '➕', label: 'Add these up', sub: 'Rows or section totals' },
  { key: 'left', emoji: '➖', label: "What's left after…", sub: 'Start from one, take others away' },
  { key: 'share', emoji: '➗', label: 'A share of…', sub: 'Half, a third, or a %' },
];

const PARTS = [['half', '½'], ['third', '⅓'], ['quarter', '¼'], ['pct', 'Custom %']];
const PART_WORD = { half: '½', third: '⅓', quarter: '¼' };

const Box = ({ on, multi, tone }) => (
  <span
    className="flex-shrink-0 flex items-center justify-center"
    style={{
      width: '20px', height: '20px',
      borderRadius: multi ? '6px' : '50%',
      border: on ? (multi ? 'none' : '6px solid ' + tone.fg) : '1.5px solid #D9CCD3',
      background: on && multi ? tone.fg : '#fff',
    }}
  >
    {on && multi && <Check className="w-3.5 h-3.5 text-white" />}
  </span>
);

/**
 * Everything above the row, grouped by section. Each section's total comes
 * first in its group, since "Income minus Bills" is the usual pick. The row's
 * own section and the first section start open; the rest sit behind a tap so
 * the list stays short.
 */
const RefList = ({ sheet, options, values, selected, multi, onPick, openIds, tone, exclude = [] }) => {
  const groups = [];
  for (const o of options) {
    if (exclude.includes(o.ref)) continue;
    const sec = sheet.sections.find((s) => s.id === o.sectionId);
    if (o.kind === 'sec' && sec && sec.showTotal === false && !selected.includes(o.ref)) continue;
    let g = groups[groups.length - 1];
    if (!g || g.sectionId !== o.sectionId) {
      g = { sectionId: o.sectionId, title: sec ? sec.title : '', total: null, rows: [] };
      groups.push(g);
    }
    if (o.kind === 'sec') g.total = o; else g.rows.push(o);
  }

  const [open, setOpen] = useState(() => {
    const m = {};
    groups.forEach((g, i) => {
      const picked = [g.total, ...g.rows].some((o) => o && selected.includes(o.ref));
      if (i === 0 || openIds.includes(g.sectionId) || picked) m[g.sectionId] = true;
    });
    return m;
  });

  if (groups.length === 0) {
    return (
      <p className="m-0 mb-2 text-[13px] text-gray-500 leading-snug">
        There's nothing above this row yet. Rows can only use things above them.
      </p>
    );
  }

  const Item = ({ o }) => {
    const on = selected.includes(o.ref);
    return (
      <button
        type="button"
        onClick={() => onPick(o.ref)}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left border-t theme-border-100"
      >
        <Box on={on} multi={multi} tone={tone} />
        <span
          className="flex-1 min-w-0 text-[14px] truncate"
          style={{ color: INK, fontWeight: o.kind === 'sec' ? 700 : 400 }}
        >
          {o.kind === 'sec' ? 'Section total' : refLabel(sheet, o.ref)}
        </span>
        <span className="text-[13px] text-gray-500 whitespace-nowrap">{formatMoney(values[o.ref])}</span>
      </button>
    );
  };

  return (
    <div className="rounded-2xl border theme-border-100 overflow-hidden mb-2">
      {groups.map((g, gi) => {
        const isOpen = !!open[g.sectionId];
        const all = g.total ? [g.total, ...g.rows] : g.rows;
        const picked = all.filter((o) => selected.includes(o.ref)).length;
        return (
          <div key={g.sectionId}>
            <button
              type="button"
              onClick={() => setOpen({ ...open, [g.sectionId]: !isOpen })}
              className={'w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left'
                + (gi === 0 ? '' : ' border-t theme-border-100')}
              style={{ background: '#FCF9FB' }}
            >
              <span className="min-w-0 truncate text-[11px] font-bold uppercase tracking-[0.06em] text-gray-500">
                {g.title}
              </span>
              <span className="flex items-center gap-1 text-[12px] text-gray-400 flex-shrink-0">
                {!isOpen && (picked > 0 ? picked + ' picked' : all.length + (all.length === 1 ? ' item' : ' items'))}
                <ChevronDown className={'w-3.5 h-3.5 transition-transform' + (isOpen ? ' rotate-180' : '')} />
              </span>
            </button>
            {isOpen && all.map((o) => <Item key={o.ref} o={o} />)}
          </div>
        );
      })}
    </div>
  );
};

const RowForm = ({
  sheet, sectionId, row, sectionTitle, tone, accent, track, onSave, onDelete, onClose,
  readOnly, deleteWarning,
}) => {
  const c = row && row.calc ? row.calc : null;
  const [name, setName] = useState(row ? row.name || '' : '');
  const [mode, setMode] = useState(c ? c.type : 'typed');
  // A worked-out row switched back to typing starts from the number it shows.
  const [amount, setAmount] = useState(() => {
    if (!row) return '';
    if (c) return String(computeSheet(sheet).rows[row.id] || 0);
    return row.amount != null ? String(row.amount) : '';
  });
  const [sumRefs, setSumRefs] = useState(c && c.type === 'sum' ? c.refs || [] : []);
  const [from, setFrom] = useState(c && c.type === 'left' ? c.from : null);
  const [minus, setMinus] = useState(c && c.type === 'left' ? c.minus || [] : []);
  const [of, setOf] = useState(c && c.type === 'share' ? c.of : null);
  const [part, setPart] = useState(c && c.type === 'share' ? c.part : 'half');
  const [pct, setPct] = useState(c && c.type === 'share' && c.part === 'pct' ? String(c.pct) : '');
  const [note, setNote] = useState(row ? row.note || '' : '');
  const [below, setBelow] = useState(row ? !!row.below : false);
  const trimmed = name.trim();

  // What this row may use, given where it sits - including the draft
  // "counted or not" choice, which moves it above or below the total line.
  const options = refsAbove(sheet, sectionId, row ? row.id : null, below);
  const order = options.map((o) => o.ref);
  const keep = (list) => order.filter((r) => list.includes(r));
  const one = (r) => (r && order.includes(r) ? r : null);
  const toggleIn = (list, set) => (ref) => set(list.includes(ref) ? list.filter((x) => x !== ref) : [...list, ref]);

  let calc = null;
  if (mode === 'sum') calc = { type: 'sum', refs: keep(sumRefs) };
  if (mode === 'left') calc = { type: 'left', from: one(from), minus: keep(minus).filter((r) => r !== one(from)) };
  if (mode === 'share') calc = { type: 'share', of: one(of), part, pct: part === 'pct' ? parseAmount(pct) : null };

  const ready = mode === 'typed'
    || (mode === 'sum' && calc.refs.length > 0)
    || (mode === 'left' && !!calc.from)
    || (mode === 'share' && !!calc.of && (part !== 'pct' || calc.pct > 0));

  // Preview by computing the whole sheet as if saved, so what it says is
  // exactly what the row will show.
  const draft = { name: trimmed || 'Untitled', note, below, calc, amount: parseAmount(amount) };
  const preview = computeSheet(withRow(sheet, sectionId, row ? row.id : null, draft));
  const previewValue = preview.rows[row ? row.id : '__draft__'] || 0;
  const V = (r) => formatMoney(preview.values[r]);
  let working = '';
  if (ready && mode === 'sum') working = calc.refs.map(V).join(' + ');
  if (ready && mode === 'left') working = [calc.from, ...calc.minus].map(V).join(' − ');
  if (ready && mode === 'share') {
    working = (part === 'pct' ? calc.pct + '%' : PART_WORD[part]) + ' of ' + V(calc.of);
  }

  const openIds = [sectionId];
  const listProps = { sheet, options, values: preview.values, openIds, tone };
  const Q = ({ children }) => (
    <p className="m-0 mt-4 mb-2 text-[12px] font-bold uppercase tracking-[0.08em]" style={{ color: tone.fg }}>
      {children}
    </p>
  );

  return (
    <Sheet
      title={readOnly ? 'How this row works' : row ? 'Edit row' : 'New row'}
      subtitle={sectionTitle}
      onClose={onClose}
      track={track}
    >
      <FieldLabel>Name</FieldLabel>
      <input
        className={SHEET_INPUT_CLS}
        style={SHEET_INPUT_STYLE}
        value={name}
        maxLength={60}
        placeholder="e.g. Rent"
        onChange={(e) => setName(e.target.value)}
      />

      <Q>How is this worked out?</Q>
      <div className="grid grid-cols-2 gap-2">
        {MODES.map((m) => {
          const on = mode === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => setMode(m.key)}
              className="rounded-2xl px-3 py-2.5 text-left"
              style={{
                border: '1.5px solid ' + (on ? tone.fg : '#EDE3E8'),
                background: on ? tone.bg : '#fff',
              }}
            >
              <span className="block text-[13.5px]" style={{ color: on ? tone.fg : INK, fontWeight: 600 }}>
                {m.emoji} {m.label}
              </span>
              <span className="block text-[11.5px] text-gray-500 mt-0.5 leading-snug">{m.sub}</span>
            </button>
          );
        })}
      </div>

      {mode === 'typed' && (
        <>
          <FieldLabel className="mt-4">Amount</FieldLabel>
          <input
            className={SHEET_INPUT_CLS}
            style={SHEET_INPUT_STYLE}
            value={amount}
            type="text"
            inputMode="decimal"
            placeholder={getCurrentCurrency().symbol + '0.00'}
            onChange={(e) => setAmount(e.target.value)}
          />
        </>
      )}

      {mode === 'sum' && (
        <>
          <Q>Add up</Q>
          <RefList {...listProps} key="sum" multi selected={sumRefs} onPick={toggleIn(sumRefs, setSumRefs)} />
        </>
      )}

      {mode === 'left' && (
        <>
          <Q>Start from</Q>
          <RefList {...listProps} key="from" selected={from ? [from] : []} onPick={(r) => setFrom(r)} />
          <Q>Take away</Q>
          <RefList
            {...listProps}
            key="minus"
            multi
            selected={minus}
            exclude={from ? [from] : []}
            onPick={toggleIn(minus, setMinus)}
          />
        </>
      )}

      {mode === 'share' && (
        <>
          <Q>A share of</Q>
          <RefList {...listProps} key="of" selected={of ? [of] : []} onPick={(r) => setOf(r)} />
          <Q>How much?</Q>
          <div className="flex flex-wrap gap-2">
            {PARTS.map(([k, lab]) => (
              <button
                key={k}
                type="button"
                onClick={() => setPart(k)}
                className="rounded-full px-4 py-2 text-[14px]"
                style={{
                  fontWeight: 600,
                  border: '1.5px solid ' + (part === k ? tone.fg : '#EDE3E8'),
                  background: part === k ? tone.fg : '#fff',
                  color: part === k ? '#fff' : INK,
                }}
              >
                {lab}
              </button>
            ))}
          </div>
          {part === 'pct' && (
            <input
              className={SHEET_INPUT_CLS + ' mt-3'}
              style={SHEET_INPUT_STYLE}
              value={pct}
              type="text"
              inputMode="decimal"
              placeholder="e.g. 60 for 60%"
              onChange={(e) => setPct(e.target.value)}
            />
          )}
        </>
      )}

      {mode !== 'typed' && (
        <>
          <p className="m-0 mt-2 text-[12px] text-gray-500">Only rows above this one can be used.</p>
          <div className="rounded-2xl px-4 py-3 mt-3" style={{ background: tone.bg }}>
            {ready ? (
              <>
                <p className="m-0 text-[12.5px] leading-snug" style={{ color: tone.fg }}>{working}</p>
                <div className="flex items-baseline justify-between mt-1.5">
                  <span className="text-[13px]" style={{ color: tone.fg, fontWeight: 700 }}>This row will show</span>
                  <span style={{ ...SERIF, fontSize: '22px', color: tone.fg }}>{formatMoney(previewValue)}</span>
                </div>
              </>
            ) : (
              <p className="m-0 text-[13px]" style={{ color: tone.fg }}>
                {mode === 'sum' && 'Tick the rows or totals to add up.'}
                {mode === 'left' && 'Pick what to start from.'}
                {mode === 'share' && (calc.of ? 'Enter a percentage.' : 'Pick what to take a share of.')}
              </p>
            )}
          </div>
        </>
      )}

      <div className="mt-4 rounded-2xl border theme-border-100 overflow-hidden">
        <MenuRow
          first
          label="Count in the section total"
          sub={below ? 'Shown under the total line, not added in' : 'Added into this section’s total'}
          right={<Toggle on={!below} accent={accent} />}
          onClick={() => setBelow(!below)}
        />
      </div>

      <FieldLabel className="mt-4">Note (optional)</FieldLabel>
      <input
        className={SHEET_INPUT_CLS + ' mb-5'}
        style={SHEET_INPUT_STYLE}
        value={note}
        maxLength={120}
        placeholder="e.g. Goes to my savings account"
        onChange={(e) => setNote(e.target.value)}
      />

      {readOnly ? (
        <>
          <p className="m-0 mb-4 text-[12.5px] text-gray-500 leading-snug">
            This is the example sheet, so nothing you change here is saved. Try
            the options above to see how the number changes.
          </p>
          <PrimaryButton accent={accent} onClick={onClose}>Close</PrimaryButton>
        </>
      ) : (
      <PrimaryButton
        disabled={!trimmed || !ready}
        accent={accent}
        onClick={() => onSave({
          name: trimmed,
          note: note.trim(),
          below,
          calc,
          amount: mode === 'typed' ? parseAmount(amount) : previewValue,
        })}
      >
        {row ? 'Save' : 'Add row'}
      </PrimaryButton>
      )}

      {row && !readOnly && (
        <DeleteButton
          label="Delete row"
          title={'Delete \u201c' + (row.name || 'this row') + '\u201d?'}
          body="This row will be deleted. This can't be undone."
          warning={deleteWarning}
          onDelete={onDelete}
        />
      )}
    </Sheet>
  );
};

/* ------------------------------------------------------------------ *
 * One row line inside a section card
 * ------------------------------------------------------------------ */

/**
 * Worked-out amounts take the section's colour with a small =; the recipe
 * underneath is grey, so colour marks "worked out" without shouting.
 * `tinted` is a row under the total line: it sits on a pale tint of the
 * section colour, bolder, with its amount in the serif like the total.
 */
const RowLine = ({ row, value, recipe, tone, first, onClick, tinted }) => (
  <button
    type="button"
    onClick={onClick}
    className={'w-full flex items-start justify-between gap-3 px-4 py-3 text-left'
      + (first || tinted ? '' : ' border-t theme-border-100')}
    style={tinted && !first ? { borderTop: '1px solid rgba(255,255,255,.9)' } : undefined}
  >
    <span className="min-w-0 flex-1">
      <span className="block text-[14.5px]" style={{ color: INK, fontWeight: tinted ? 600 : 500 }}>
        {row.name || 'Untitled'}
      </span>
      {row.calc && recipe && (
        <span className="block text-[12px] text-gray-500 mt-0.5 leading-snug">{recipe}</span>
      )}
      {row.note && (
        <span className="block text-[12px] text-gray-500 mt-0.5 leading-snug">{row.note}</span>
      )}
    </span>
    {tinted ? (
      <span className="whitespace-nowrap" style={{ ...SERIF, fontSize: '19px', color: tone.fg }}>
        {formatMoney(value)}
      </span>
    ) : (
      <span
        className="flex items-center gap-1.5 text-[14.5px] whitespace-nowrap"
        style={{ color: row.calc ? tone.fg : INK, fontWeight: 500 }}
      >
        {row.calc && (
          <span
            className="inline-flex items-center justify-center rounded-full text-[11px]"
            style={{ width: '18px', height: '18px', background: tone.soft, color: tone.fg, fontWeight: 700 }}
          >
            =
          </span>
        )}
        {formatMoney(value)}
      </span>
    )}
  </button>
);

// plan_06: Money plan row inside a sheet - calm text, lilac worked-out amounts.
const PlanSheetRow = ({ row, value, recipe, first, onClick, tinted }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-start justify-between text-left"
    style={{ gap: '12px', padding: '13px 0', background: 'transparent', borderTop: first ? 'none' : '1px solid ' + (tinted ? '#FFFFFF' : '#EEEAF6') }}
  >
    <span className="min-w-0 flex-1">
      <span className="block" style={{ fontSize: '15px', color: '#3E3C47', fontWeight: tinted ? 600 : 500 }}>{row.name || 'Untitled'}</span>
      {row.calc && recipe && (
        <span className="block" style={{ fontSize: '12.5px', color: '#8B879A', marginTop: '3px', lineHeight: 1.4 }}>{recipe}</span>
      )}
      {row.note && (
        <span className="block" style={{ fontSize: '12.5px', color: '#8B879A', marginTop: '3px', lineHeight: 1.4 }}>{row.note}</span>
      )}
    </span>
    <span className="flex items-center whitespace-nowrap" style={{ gap: '7px', fontSize: '15px', fontWeight: 600, color: row.calc ? '#7A5DD3' : '#26252E' }}>
      {row.calc && (
        <span style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#ECE6FA', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>=</span>
      )}
      {formatMoney(value)}
    </span>
  </button>
);

/* ------------------------------------------------------------------ *
 * A row in "Reorder rows" mode - a grip to drag, nothing to tap
 * ------------------------------------------------------------------ */

const SortableRow = ({ row, value, tone, first }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });
  return (
    <div
      ref={setNodeRef}
      className={'flex items-center gap-2 pl-2 pr-4 py-2.5 bg-white' + (first ? '' : ' border-t theme-border-100')}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        position: 'relative',
        zIndex: isDragging ? 2 : 'auto',
      }}
    >
      {/* The grip is the only drag target, so the page still scrolls when
          you touch anywhere else on the row. */}
      <span
        {...attributes}
        {...listeners}
        className="w-9 h-9 flex items-center justify-center flex-shrink-0"
        style={{ touchAction: 'none', color: '#B8AFB4' }}
      >
        <GripVertical className="w-4 h-4" />
      </span>
      <span className="flex-1 min-w-0 text-[14.5px] truncate" style={{ color: INK, fontWeight: 500 }}>
        {row.name || 'Untitled'}
      </span>
      <span className="text-[14px] whitespace-nowrap" style={{ color: row.calc ? tone.fg : INK, fontWeight: 500 }}>
        {formatMoney(value)}
      </span>
    </div>
  );
};

/* ------------------------------------------------------------------ *
 * The explainer - same shape as every other info sheet in the app
 * ------------------------------------------------------------------ */

const INFO_POINTS = [
  ['🧾', 'Plan it your own way',
    'A budget sheet is for planning outside your monthly budget. Name rows however you like, group them into sections, and each section adds itself up.'],
  ['✏️', 'Type it, or let it be worked out',
    'Every row is either a number you type, or worked out from other rows: added up, what’s left after taking some away, or a share like half or 10%. Worked-out rows are shown in colour with an = sign, and the working is written underneath.'],
  ['⬆️', 'Rows use what’s above them',
    'A worked-out row can only use rows and totals above it on the page. When those numbers change, it updates by itself.'],
  ['➖', 'Under the total line',
    'Turn off “Count in the section total” to show a row under the total, like how a total splits, without it being added in twice.'],
  ['🗑️', 'Nothing breaks',
    'If you delete something a worked-out row uses, you’ll be told first. That row keeps its last amount and becomes a typed row.'],
  ['📌', 'Kept, not monthly',
    'Sheets aren’t tied to a month or a year. Pin the one you use most, and duplicate a sheet to try out changes without touching the original.'],
];

const InfoSheet = ({ onClose, onExample, accent }) => createPortal(
  <div className="fixed inset-0 z-50" style={{ touchAction: 'none' }}>
    <div className="absolute inset-0 bg-black/40" onClick={onClose} />
    <div
      className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 pb-10 shadow-xl overflow-y-auto"
      style={{ maxHeight: '88vh', touchAction: 'pan-y' }}
    >
      <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
      <h3 className="font-normal text-gray-800 mb-4" style={{ ...SERIF, fontSize: '20px' }}>About budget sheets</h3>
      <div className="space-y-4">
        {INFO_POINTS.map(([emoji, title, text]) => (
          <div key={title} className="flex gap-3">
            <span className="text-xl flex-shrink-0">{emoji}</span>
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-0.5">{title}</p>
              <p className="text-xs text-gray-500 leading-relaxed">{text}</p>
            </div>
          </div>
        ))}
      </div>
      {onExample && (
        <button
          onClick={onExample}
          className="mt-6 w-full py-3 rounded-2xl font-semibold text-sm"
          style={{ border: '1.5px solid ' + accent, color: accent, background: '#fff' }}
        >
          See an example sheet
        </button>
      )}
      <button onClick={onClose} className="mt-3 w-full py-3 rounded-2xl theme-bg-500 text-white font-semibold text-sm">Got it</button>
    </div>
  </div>,
  document.body
);

/* ------------------------------------------------------------------ *
 * The tab
 * ------------------------------------------------------------------ */

const BudgetSheets = ({
  sheets, onChange, openId, onOpen, theme, accent, track, eyebrow, muted, palette, cardCls,
  infoOpen, onInfoChange,
}) => {
  // { kind: 'sheet' | 'section' | 'row', id, sectionId }. Forms hold ids, not
  // objects, so a toggle inside an open form reflects the saved state live.
  const [form, setForm] = useState(null);
  const [reorderId, setReorderId] = useState(null);   // section being reordered
  const todayKey = todayLocalKey();
  const isPink = theme === 'pink';
  const isWarm = theme === 'warm';
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  // plan_06: Money plan - black buttons in the pop-ups.
  const plan = usePlan();
  const formAccent = plan ? '#1F1E24' : accent;

  const isExample = openId === EXAMPLE_ID;
  const open = isExample ? EXAMPLE_SHEET : sheets.find((s) => s.id === openId) || null;
  const showInfo = (v) => onInfoChange && onInfoChange(v);

  // Every change goes through here, so this is the one place a worked-out row
  // whose ingredients have gone gets frozen at the amount it last showed.
  const replaceSheet = (next) => {
    if (next.id === EXAMPLE_ID) return;   // the example is never saved
    const prev = sheets.find((s) => s.id === next.id) || null;
    const { sheet } = settleSheet(prev, next);
    onChange(sheets.map((s) => (s.id === next.id ? { ...sheet, updatedAt: todayKey } : s)));
  };
  const updateOpen = (fn) => { if (open && !isExample) replaceSheet(fn(open)); };

  /* ---- sheets ---- */
  const createSheet = ({ name, emoji }) => {
    const s = {
      id: newId('bs'), name, emoji, pinned: false,
      createdAt: todayKey, updatedAt: todayKey, sections: [],
    };
    onChange([s, ...sheets]);
    setForm(null);
    onOpen(s.id);
  };
  const duplicateSheet = (sheet, name) => {
    // Ids inside a sheet only need to be unique within it, so a straight deep
    // copy keeps every row reference intact.
    const copy = {
      ...JSON.parse(JSON.stringify(sheet)),
      id: newId('bs'),
      name: (name || sheet.name + ' copy').slice(0, 40),
      pinned: false,
      createdAt: todayKey,
      updatedAt: todayKey,
    };
    onChange([copy, ...sheets]);
    setForm(null);
    setReorderId(null);
    onOpen(copy.id);
  };
  const deleteSheet = (sheet) => {
    onChange(sheets.filter((s) => s.id !== sheet.id));
    setForm(null);
    onOpen(null);
  };
  const openExample = () => {
    showInfo(false);
    setForm(null);
    setReorderId(null);
    onOpen(EXAMPLE_ID);
  };

  /* ---- sections ---- */
  const saveSection = (id, patch) => {
    updateOpen((s) => ({
      ...s,
      sections: id
        ? s.sections.map((sec) => (sec.id === id ? { ...sec, ...patch } : sec))
        : [...s.sections, { id: newId('sec'), rows: [], ...patch }],
    }));
    setForm(null);
  };
  const sectionsMoved = (sheet, id, dir) => {
    const list = sheet.sections.slice();
    const i = list.findIndex((sec) => sec.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return null;
    [list[i], list[j]] = [list[j], list[i]];
    return { ...sheet, sections: list };
  };
  const moveSection = (id, dir) => {
    if (!open) return;
    const next = sectionsMoved(open, id, dir);
    if (next && moveBreaks(open, next).length === 0) replaceSheet(next);
  };
  const deleteSection = (id) => {
    updateOpen((s) => ({ ...s, sections: s.sections.filter((sec) => sec.id !== id) }));
    setForm(null);
    setReorderId(null);
  };

  /* ---- rows ---- */
  const saveRow = (sectionId, id, patch) => {
    updateOpen((s) => ({
      ...s,
      sections: s.sections.map((sec) => {
        if (sec.id !== sectionId) return sec;
        const rows = sec.rows || [];
        return {
          ...sec,
          rows: id
            ? rows.map((r) => (r.id === id ? { ...r, ...patch } : r))
            : [...rows, { id: newId('row'), below: false, calc: null, ...patch }],
        };
      }),
    }));
    setForm(null);
  };
  const deleteRow = (sectionId, id) => {
    updateOpen((s) => ({
      ...s,
      sections: s.sections.map((sec) => (sec.id === sectionId
        ? { ...sec, rows: (sec.rows || []).filter((r) => r.id !== id) }
        : sec)),
    }));
    setForm(null);
  };

  /**
   * Drag within one group - the counted rows, or the rows under the total
   * line. Crossing the line is what the "Count in the section total" switch
   * is for. A drop that would leave a worked-out row above something it uses
   * is refused, with the reason, rather than quietly changing that row.
   */
  const onRowDragEnd = (sec) => ({ active, over }) => {
    if (!open || !over || active.id === over.id) return;
    const rows = sec.rows || [];
    const a = rows.find((r) => r.id === active.id);
    const b = rows.find((r) => r.id === over.id);
    if (!a || !b || !!a.below !== !!b.below) return;
    const next = {
      ...open,
      sections: open.sections.map((x) => (x.id === sec.id
        ? { ...x, rows: arrayMove(rows, rows.indexOf(a), rows.indexOf(b)) }
        : x)),
    };
    const breaks = moveBreaks(open, next);
    if (breaks.length) {
      customToast.error("Can't move it there", blockedMove(breaks) + '.');
      return;
    }
    replaceSheet(next);
  };

  /* ---- forms ---- */
  const formSheet = form && form.kind === 'sheet' && form.id
    ? sheets.find((s) => s.id === form.id) || null : null;
  const formSectionIndex = form && form.kind === 'section' && form.id && open
    ? open.sections.findIndex((sec) => sec.id === form.id) : -1;
  const formSection = formSectionIndex >= 0 ? open.sections[formSectionIndex] : null;
  const rowSection = form && form.kind === 'row' && open
    ? open.sections.find((sec) => sec.id === form.sectionId) || null : null;
  const formRow = rowSection && form.id
    ? (rowSection.rows || []).find((r) => r.id === form.id) || null : null;
  // Each section takes a tile colour in turn - pink, green, blue, lilac on
  // the pink theme, and the same slots on warm and blue - used for its TOTAL
  // box, its worked-out amounts, and the pale tint behind rows under the total.
  const sectionTone = (index) => {
    const t = palette[SECTION_SLOTS[(index < 0 ? 0 : index) % SECTION_SLOTS.length]];
    return { fg: t.value, bg: t.bg, soft: softTint(t.bg) };
  };

  // Moves that would break a row are known before the button is tapped.
  const breaksIf = (dir) => {
    if (!formSection || !open) return null;
    const next = sectionsMoved(open, formSection.id, dir);
    const names = next ? moveBreaks(open, next) : [];
    return names.length ? names : null;
  };

  const forms = (
    <>
      {form && form.kind === 'sheet' && (
        <SheetForm
          key={form.id || 'new'}
          sheet={formSheet}
          accent={formAccent}
          track={track}
          onSave={(patch) => {
            if (formSheet) { replaceSheet({ ...formSheet, ...patch }); setForm(null); }
            else createSheet(patch);
          }}
          onPin={() => formSheet && replaceSheet({ ...formSheet, pinned: !formSheet.pinned })}
          onDuplicate={() => formSheet && duplicateSheet(formSheet)}
          onDelete={() => formSheet && deleteSheet(formSheet)}
          onClose={() => setForm(null)}
        />
      )}
      {form && form.kind === 'section' && (
        <SectionForm
          key={form.id || 'new'}
          section={formSection}
          index={formSectionIndex}
          count={open ? open.sections.length : 0}
          accent={formAccent}
          track={track}
          blockUp={breaksIf(-1)}
          blockDown={breaksIf(1)}
          deleteWarning={formSection ? dependWarning(dependentsOf(
            open,
            [secRef(formSection.id), ...(formSection.rows || []).map((r) => rowRef(r.id))],
            (formSection.rows || []).map((r) => r.id),
          )) : null}
          onReorder={() => { if (formSection) { setReorderId(formSection.id); setForm(null); } }}
          onSave={(patch) => saveSection(formSection ? formSection.id : null, patch)}
          onMove={(dir) => formSection && moveSection(formSection.id, dir)}
          onDelete={() => formSection && deleteSection(formSection.id)}
          onClose={() => setForm(null)}
        />
      )}
      {form && form.kind === 'row' && rowSection && (
        <RowForm
          key={form.id || 'new'}
          sheet={open}
          sectionId={rowSection.id}
          row={formRow}
          sectionTitle={rowSection.title}
          tone={sectionTone(open.sections.indexOf(rowSection))}
          accent={formAccent}
          track={track}
          readOnly={isExample}
          deleteWarning={formRow ? dependWarning(dependentsOf(open, [rowRef(formRow.id)], [formRow.id])) : null}
          onSave={(patch) => saveRow(rowSection.id, formRow ? formRow.id : null, patch)}
          onDelete={() => formRow && deleteRow(rowSection.id, formRow.id)}
          onClose={() => setForm(null)}
        />
      )}
      {infoOpen && (
        <InfoSheet
          accent={accent}
          onClose={() => showInfo(false)}
          onExample={isExample ? null : openExample}
        />
      )}
    </>
  );

  /* ---- an open sheet (or the example) ---- */
  if (open) {
    const { rows: values, totals } = computeSheet(open);

    // plan_06: Money plan - white back circle, big title, one card per section.
    if (plan) {
      const circle = { width: '40px', height: '40px', borderRadius: '50%', background: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 0, flexShrink: 0 };
      const dots = { width: '32px', height: '32px', borderRadius: '50%', background: '#F5F2FA', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 0, flexShrink: 0 };
      const LILAC = '#7A5DD3';
      return (
        <div>
          <div className="flex items-center justify-between px-1">
            <button type="button" onClick={() => { setReorderId(null); onOpen(null); }} aria-label="Back to budget sheets" style={circle}>
              <ChevronLeft className="w-5 h-5" style={{ color: INK }} />
            </button>
            <div className="flex items-center" style={{ gap: '8px' }}>
              <button type="button" onClick={() => showInfo(true)} aria-label="About budget sheets" style={circle}>
                <span style={{ fontSize: '15px', fontWeight: 700, color: '#26252E' }}>i</span>
              </button>
              {!isExample && (
                <button type="button" onClick={() => setForm({ kind: 'sheet', id: open.id })} aria-label="Sheet options" style={circle}>
                  <MoreHorizontal className="w-5 h-5" style={{ color: '#26252E' }} />
                </button>
              )}
            </div>
          </div>
          <h1 className="m-0 px-1" style={{ marginTop: '18px', overflowWrap: 'anywhere' }}>
            {open.emoji ? open.emoji + ' ' : ''}{String(open.name || '').toLowerCase()}{/* plan_15: lowercase like the tabs */}
          </h1>
          <p className="m-0 px-1" style={{ fontSize: '14.5px', color: '#8B879A', marginTop: '6px' }}>
            {isExample ? 'Example sheet' : 'Edited ' + relDay(open.updatedAt, todayKey)}
          </p>

          {isExample && (
            <div style={{ background: '#FFFFFF', borderRadius: '28px', padding: '20px', marginTop: '16px' }}>
              <p className="m-0" style={{ fontSize: '15.5px', fontWeight: 600, color: '#26252E' }}>An example monthly budget calculation</p>
              <p className="m-0" style={{ fontSize: '14px', color: '#8B879A', marginTop: '6px', lineHeight: 1.5 }}>
                Tap any row to see how it&rsquo;s set up. Amounts in colour are worked out from the rows above them.
              </p>
              <button
                type="button"
                onClick={() => duplicateSheet(EXAMPLE_SHEET, 'Monthly plan')}
                className="w-full"
                style={{ marginTop: '16px', background: '#1F1E24', color: '#FFFFFF', borderRadius: '999px', padding: '15px', fontSize: '15px', fontWeight: 600, border: 0 }}
              >
                Make a copy to edit
              </button>
            </div>
          )}

          {open.sections.map((sec, si) => {
            const rows = sec.rows || [];
            const counted = rows.filter((r) => !r.below);
            const below = rows.filter((r) => r.below);
            const showTotal = sec.showTotal !== false && counted.length > 0;
            const reordering = reorderId === sec.id && !isExample;
            const openRow = (r) => setForm({ kind: 'row', sectionId: sec.id, id: r.id });
            const t = sectionTone(si);
            const canAdd = !isExample && !reordering;
            return (
              <div key={sec.id} style={{ background: '#FFFFFF', borderRadius: '28px', padding: '20px 20px 0', marginTop: '16px', overflow: 'hidden' }}>
                <div className="flex items-center justify-between" style={{ gap: '10px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '18px', fontWeight: 700, letterSpacing: '-0.6px', color: '#26252E', minWidth: 0, overflowWrap: 'anywhere' }}>{sec.title}</span>
                  {!isExample && !reordering && (
                    <button type="button" onClick={() => setForm({ kind: 'section', id: sec.id })} aria-label="Section options" style={dots}>
                      <MoreHorizontal className="w-4 h-4" style={{ color: '#8B879A' }} />
                    </button>
                  )}
                </div>
                {reordering ? (
                  <>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onRowDragEnd(sec)}>
                      <SortableContext items={counted.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                        {counted.map((r, i) => (
                          <SortableRow key={r.id} row={r} value={values[r.id]} tone={t} first={i === 0} />
                        ))}
                      </SortableContext>
                      {below.length > 0 && (
                        <p className="m-0" style={{ padding: '12px 0 4px', fontSize: '12px', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: '#8B879A', borderTop: '1px solid #EEEAF6' }}>
                          Not in the total
                        </p>
                      )}
                      <SortableContext items={below.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                        {below.map((r) => (
                          <SortableRow key={r.id} row={r} value={values[r.id]} tone={t} first />
                        ))}
                      </SortableContext>
                    </DndContext>
                    <p className="m-0" style={{ fontSize: '12.5px', color: '#8B879A', padding: '10px 0 0' }}>
                      Drag the grip to move a row.
                    </p>
                    <button
                      type="button"
                      onClick={() => setReorderId(null)}
                      className="w-full"
                      style={{ margin: '12px 0 18px', background: '#1F1E24', color: '#FFFFFF', borderRadius: '999px', padding: '13px', fontSize: '14.5px', fontWeight: 600, border: 0 }}
                    >
                      Done
                    </button>
                  </>
                ) : (
                  <>
                    {counted.map((r, i) => (
                      <PlanSheetRow
                        key={r.id}
                        row={r}
                        value={values[r.id]}
                        recipe={describeCalc(open, r.calc)}
                        first={i === 0}
                        onClick={() => openRow(r)}
                      />
                    ))}
                    {canAdd && (
                      <button
                        type="button"
                        onClick={() => setForm({ kind: 'row', sectionId: sec.id, id: null })}
                        className="w-full text-left"
                        style={{ padding: '13px 0', background: 'transparent', color: LILAC, fontWeight: 600, fontSize: '14.5px', borderTop: counted.length > 0 ? '1px solid #EEEAF6' : 'none' }}
                      >
                        + Add a row
                      </button>
                    )}
                    {showTotal && (
                      <div className="flex items-center justify-between" style={{ background: '#F7F5FB', margin: '6px -20px 0', padding: '16px 20px' }}>
                        <span style={{ fontSize: '13px', color: '#8B879A', fontWeight: 600, letterSpacing: '1.2px', textTransform: 'uppercase' }}>Total</span>
                        <span style={{ fontSize: '20px', fontWeight: 700, color: LILAC }}>{formatMoney(totals[sec.id])}</span>
                      </div>
                    )}
                    {below.length > 0 && (
                      <div style={{ background: '#F2EEFB', margin: showTotal ? '0 -20px' : '6px -20px 0', padding: '0 20px' }}>
                        {below.map((r, i) => (
                          <PlanSheetRow
                            key={r.id}
                            row={r}
                            value={values[r.id]}
                            recipe={describeCalc(open, r.calc)}
                            tinted
                            first={i === 0}
                            onClick={() => openRow(r)}
                          />
                        ))}
                      </div>
                    )}
                    {!showTotal && below.length === 0 && <div style={{ height: '8px' }} />}
                  </>
                )}
              </div>
            );
          })}

          {!isExample && open.sections.length === 0 && (
            <div style={{ background: '#FFFFFF', borderRadius: '28px', padding: '20px', marginTop: '16px' }}>
              <p className="m-0" style={{ fontSize: '14px', color: '#8B879A', lineHeight: 1.5 }}>
                Sections group your rows, like Income, Bills or Savings. Each one adds itself up.
              </p>
            </div>
          )}

          {!isExample && (
            <button
              type="button"
              onClick={() => setForm({ kind: 'section', id: null })}
              className="w-full"
              style={{ marginTop: '16px', border: '2px dashed #DCD3F4', background: 'none', borderRadius: '24px', padding: '18px', fontSize: '15px', fontWeight: 600, color: LILAC }}
            >
              + Add a section
            </button>
          )}
          <div style={{ height: '24px' }} />
          {forms}
        </div>
      );
    }
    return (
      <div>
        <div className="flex items-center gap-2.5 px-1">
          <button
            type="button"
            onClick={() => { setReorderId(null); onOpen(null); }}
            aria-label="Back to budget sheets"
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: track }}
          >
            <ChevronLeft className="w-4 h-4" style={{ color: accent }} />
          </button>
          <h1
            className="m-0 min-w-0 truncate"
            style={{ ...SERIF, fontSize: '26px', color: '#1A1A1F', letterSpacing: '-0.6px' }}
          >
            {open.emoji ? open.emoji + ' ' : ''}{open.name}
          </h1>
          <InfoDot onClick={() => showInfo(true)} label="About budget sheets" />
          <span className="flex-1" />
          {!isExample && (
            <button
              type="button"
              onClick={() => setForm({ kind: 'sheet', id: open.id })}
              aria-label="Sheet options"
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: track }}
            >
              <MoreHorizontal className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>
        <p className="m-0 text-[12.5px] px-1 mt-1" style={{ color: muted }}>
          {isExample ? 'Example sheet' : 'Edited ' + relDay(open.updatedAt, todayKey)}
        </p>

        {isExample && (
          // Plain white card like the rest of the page - the colour is kept
          // for the worked-out amounts, so the banner doesn't compete with them.
          <div className={cardCls + ' p-4 mt-4'}>
            <div className="flex items-start gap-3">
              <span
                className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
                style={{ background: track }}
              >
                {'\ud83d\udcd6'}
              </span>
              <div className="min-w-0">
                <p className="m-0 text-[14.5px]" style={{ color: INK, fontWeight: 600 }}>
                  An example monthly budget calculation
                </p>
                <p className="m-0 mt-1 text-[13px] text-gray-500 leading-snug">
                  Tap any row to see how it&rsquo;s set up. Amounts in colour are
                  worked out from the rows above them.
                </p>
                <button
                  type="button"
                  onClick={() => duplicateSheet(EXAMPLE_SHEET, 'Monthly plan')}
                  className="mt-3 rounded-full px-4 py-2 text-[13.5px] font-bold text-white"
                  style={{ background: accent }}
                >
                  Make a copy to edit
                </button>
              </div>
            </div>
          </div>
        )}

        {open.sections.map((sec, si) => {
          const rows = sec.rows || [];
          const counted = rows.filter((r) => !r.below);
          const below = rows.filter((r) => r.below);
          const showTotal = sec.showTotal !== false && counted.length > 0;
          const reordering = reorderId === sec.id && !isExample;
          const openRow = (r) => setForm({ kind: 'row', sectionId: sec.id, id: r.id });
          const t = sectionTone(si);
          const canAdd = !isExample && !reordering;
          return (
            <div key={sec.id} className="mt-5">
              <div className="flex items-center justify-between px-1 mb-2">
                <p
                  className="m-0 text-[13px] font-bold uppercase tracking-[0.08em] min-w-0 truncate"
                  style={{ color: eyebrow }}
                >
                  <span className="wy-star">✦ </span>{sec.title}
                </p>
                {!isExample && !reordering && (
                  <button
                    type="button"
                    onClick={() => setForm({ kind: 'section', id: sec.id })}
                    aria-label="Section options"
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: track }}
                  >
                    <MoreHorizontal className="w-3.5 h-3.5 text-gray-500" />
                  </button>
                )}
              </div>
              <div className={cardCls + ' overflow-hidden'}>
                {reordering ? (
                  <>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onRowDragEnd(sec)}>
                      <SortableContext items={counted.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                        {counted.map((r, i) => (
                          <SortableRow key={r.id} row={r} value={values[r.id]} tone={t} first={i === 0} />
                        ))}
                      </SortableContext>
                      {below.length > 0 && (
                        <p
                          className="m-0 px-4 pt-3 pb-1 text-[10.5px] font-bold uppercase tracking-[0.08em] border-t theme-border-100"
                          style={{ color: muted }}
                        >
                          Not in the total
                        </p>
                      )}
                      <SortableContext items={below.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                        {below.map((r) => (
                          <SortableRow key={r.id} row={r} value={values[r.id]} tone={t} first />
                        ))}
                      </SortableContext>
                    </DndContext>
                    <button
                      type="button"
                      onClick={() => setReorderId(null)}
                      className="w-full px-4 py-3 text-center text-[14px] font-bold border-t theme-border-100"
                      style={{ color: accent }}
                    >
                      Done
                    </button>
                  </>
                ) : (
                  <>
                    {counted.map((r, i) => (
                      <RowLine
                        key={r.id}
                        row={r}
                        value={values[r.id]}
                        recipe={describeCalc(open, r.calc)}
                        tone={t}
                        first={i === 0}
                        onClick={() => openRow(r)}
                      />
                    ))}
                    {/* Just above the total - where a new row lands. */}
                    {canAdd && (
                      <button
                        type="button"
                        onClick={() => setForm({ kind: 'row', sectionId: sec.id, id: null })}
                        className={'w-full px-4 py-3 text-left text-[14px] font-semibold'
                          + (counted.length > 0 ? ' border-t theme-border-100' : '')}
                        style={{ color: accent }}
                      >
                        + Add a row
                      </button>
                    )}
                    {showTotal && (
                      <div
                        className="flex items-center justify-between px-4 py-3.5"
                        style={{ background: t.bg, color: t.fg }}
                      >
                        <span className="text-[12px] font-bold uppercase" style={{ letterSpacing: '0.1em' }}>
                          Total
                        </span>
                        <span style={{ ...SERIF, fontSize: '24px' }}>{formatMoney(totals[sec.id])}</span>
                      </div>
                    )}
                    {/* Under the total line: shown, not counted. A pale tint of
                        the section's own colour, since these are often the
                        numbers the section exists to work out. */}
                    {below.length > 0 && (
                      <div style={{ background: t.soft }}>
                        {below.map((r, i) => (
                          <RowLine
                            key={r.id}
                            row={r}
                            value={values[r.id]}
                            recipe={describeCalc(open, r.calc)}
                            tone={t}
                            tinted
                            first={i === 0}
                            onClick={() => openRow(r)}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              {reordering && (
                <p className="m-0 mt-1.5 px-1 text-[12px] text-gray-500">
                  Drag the grip to move a row. Tap Done when you&rsquo;re finished.
                </p>
              )}
            </div>
          );
        })}

        {!isExample && open.sections.length === 0 && (
          <div className={cardCls + ' p-4 mt-5'}>
            <p className="m-0 text-[13px] text-gray-500 leading-snug">
              Sections group your rows, like Income, Bills or Savings. Each one
              adds itself up.
            </p>
          </div>
        )}

        {!isExample && (
          <button
            type="button"
            onClick={() => setForm({ kind: 'section', id: null })}
            className="w-full mt-5 rounded-2xl py-3.5 text-[14px] font-semibold"
            style={{ border: '1.5px dashed ' + accent + '66', color: accent }}
          >
            + Add a section
          </button>
        )}
        <div style={{ height: '24px' }} />
        {forms}
      </div>
    );
  }

  /* ---- the list ---- */
  const sorted = sheets.slice().sort((a, b) => {
    if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
    const au = a.updatedAt || '';
    const bu = b.updatedAt || '';
    return au < bu ? 1 : au > bu ? -1 : 0;
  });

  // plan_06: Money plan list - one white card, then Example and New sheet.
  if (plan) {
    return (
      <div>
        <p className="m-0 px-1" style={{ fontSize: '14.5px', color: '#8B879A', lineHeight: 1.4, marginBottom: '16px' }}>
          Your monthly plans, set out like a spreadsheet
        </p>
        {sorted.length === 0 ? (
          <div style={{ background: '#FFFFFF', borderRadius: '28px', padding: '20px' }}>
            <p className="m-0" style={{ fontSize: '14px', color: '#8B879A', lineHeight: 1.5 }}>
              Plan a budget your own way: name rows however you like, add notes,
              and come back to it any time. Start one below, or have a look at
              an example first!
            </p>
          </div>
        ) : (
          <div style={{ background: '#FFFFFF', borderRadius: '28px', padding: '6px 18px' }}>
            {sorted.map((s, i) => {
              const n = (s.sections || []).length;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onOpen(s.id)}
                  className="w-full flex items-center text-left"
                  style={{ gap: '14px', padding: '13px 0', background: 'transparent', borderTop: i === 0 ? 'none' : '1px solid #EEEAF6' }}
                >
                  <span style={{ width: '42px', height: '42px', borderRadius: '14px', background: '#F5F2FA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>
                    {s.emoji || SHEET_EMOJIS[0]}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center min-w-0" style={{ gap: '6px' }}>
                      <span className="truncate" style={{ fontSize: '15.5px', fontWeight: 600, color: '#26252E' }}>{s.name || 'Untitled'}</span>
                      {s.pinned && (
                        <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '999px', background: '#ECE6FA', color: '#7A5DD3', flexShrink: 0 }}>Pinned</span>
                      )}
                    </span>
                    <span className="block" style={{ fontSize: '13px', color: '#8B879A', marginTop: '2px' }}>
                      {n} section{n === 1 ? '' : 's'} {'·'} edited {relDay(s.updatedAt, todayKey)}
                    </span>
                  </span>
                  <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#C1BCD0' }} />
                </button>
              );
            })}
          </div>
        )}
        <div className="grid grid-cols-2" style={{ gap: '10px', marginTop: '16px' }}>
          <button type="button" onClick={openExample} style={{ background: '#FFFFFF', borderRadius: '999px', padding: '16px 10px', fontSize: '14.5px', fontWeight: 600, color: '#26252E', border: 0 }}>
            {'📖'} See an example
          </button>
          <button type="button" onClick={() => setForm({ kind: 'sheet', id: null })} style={{ background: '#1F1E24', borderRadius: '999px', padding: '16px 10px', fontSize: '14.5px', fontWeight: 600, color: '#FFFFFF', border: 0 }} data-testid="budget-sheets-new">
            + New sheet
          </button>
        </div>
        {forms}
      </div>
    );
  }

  return (
    <div>
      <p className="m-0 text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2" style={{ color: eyebrow }}>
        <span className="wy-star">✦ </span>Your budget sheets
      </p>

      {sorted.length === 0 && (
        <div className={cardCls + ' p-4'}>
          <p className="m-0 text-[13px] text-gray-500 leading-snug">
            Plan a budget your own way: name rows however you like, add notes,
            and come back to it any time. Tap + to start one, or have a look at
            an example first.
          </p>
        </div>
      )}

      {sorted.map((s, i) => {
        const tile = palette[i % palette.length];
        const n = (s.sections || []).length;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onOpen(s.id)}
            className={cardCls + ' w-full p-4 mb-3 flex items-center gap-3.5 text-left'}
          >
            <span
              className="w-11 h-11 rounded-2xl flex items-center justify-center text-[22px] flex-shrink-0"
              style={{ background: tile.bg }}
            >
              {s.emoji || SHEET_EMOJIS[0]}
            </span>
            <span className="flex-1 min-w-0">
              <span className="flex items-center gap-1.5 min-w-0">
                <span className="text-[15px] font-semibold truncate" style={{ color: INK }}>
                  {s.name || 'Untitled'}
                </span>
                {s.pinned && (
                  <span
                    className="text-[10.5px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{ background: palette[0].bg, color: palette[0].value }}
                  >
                    Pinned
                  </span>
                )}
              </span>
              <span className="block text-[12.5px] text-gray-500 mt-0.5">
                {n} section{n === 1 ? '' : 's'} {'·'} edited {relDay(s.updatedAt, todayKey)}
              </span>
            </span>
            <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#C9BFC5' }} />
          </button>
        );
      })}

      {/* Always here, not just when empty - the example is most useful the
          first time a worked-out row doesn't make sense. */}
      <button
        type="button"
        onClick={openExample}
        className="w-full mt-1 py-3 flex items-center justify-center gap-1.5 text-[13.5px] font-semibold"
        style={{ color: accent }}
      >
        {'📖'} See an example sheet
      </button>

      {/* Same FAB as the rest of the page, owned here because it opens a
          different sheet. NotesTab hides its own on this tab. */}
      {!form && !infoOpen && (
        <button
          type="button"
          onClick={() => setForm({ kind: 'sheet', id: null })}
          className="fixed z-30 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 active:scale-95"
          style={{
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)',
            right: '20px',
            background: isPink
              ? 'linear-gradient(135deg, #F5A8C1 0%, #E27BA1 100%)'
              : isWarm ? '#2D5A3D' : '#4361EE',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
          data-testid="budget-sheets-fab"
        >
          <Plus className="w-6 h-6 text-white" />
        </button>
      )}
      {forms}
    </div>
  );
};

export default BudgetSheets;
